import { createSelector } from "@reduxjs/toolkit";
import { CustomerAccountRole, RewardsAccount, RewardsPrice } from "redmond";
import { IStoreState } from "../../../../reducers/types";
import { isCorpTenant } from "@capone/common";
import { config } from "../../../../api/config";

export const getIsFirstLaunch = (state: IStoreState) =>
  state.vacationRentalRewards.isFirstLaunch;

export const getAgentEmail = (state: IStoreState) =>
  state.vacationRentalRewards.agentEmail;

export const getRewardsAccounts = (state: IStoreState) =>
  state.vacationRentalRewards.rewardsAccounts;

export const getUserSelectedAccountReferenceId = (state: IStoreState) =>
  state.vacationRentalRewards.selectedAccountReferenceId;

export const getFetchRewardsAccountsCallState = (state: IStoreState) =>
  state.vacationRentalRewards.fetchRewardsAccountsCallState;

export const getUserInfo = (state: IStoreState) =>
  state.vacationRentalRewards.userInfo;

export const getRewardsAccountWithLargestValue = createSelector(
  getRewardsAccounts,
  (rewardsAccounts): RewardsAccount | undefined => {
    const sortedAccts = [...rewardsAccounts].sort((prev, current) => {
      return (
        current.rewardsCashEquivalent.value - prev.rewardsCashEquivalent.value
      );
    });
    return sortedAccts[0];
  }
);

export const getRewardsAccountWithLargestEarnForBanner = createSelector(
  getRewardsAccounts,
  (rewardsAccounts): RewardsAccount => rewardsAccounts[0]
);

// the rewards account selection doesn't have a default state where none of the available options is selected
export const getDefaultSelectedAccountReferenceId = createSelector(
  getRewardsAccounts,
  (rewardsAccounts): string | null => {
    // return the id with largest earn
    if (rewardsAccounts.length > 0) {
      return rewardsAccounts[0].accountReferenceId;
    }
    return null;
  }
);

// this is the primary selector used outside the component
// either returns the user selected account id, or the default from above if not present
export const getSelectedAccountReferenceId = createSelector(
  getUserSelectedAccountReferenceId,
  getDefaultSelectedAccountReferenceId,
  (
    userSelectedAccountReferenceId,
    defaultSelectedAccountReferenceId
  ): string | null => {
    return userSelectedAccountReferenceId ?? defaultSelectedAccountReferenceId;
  }
);

export const getSelectedAccountReferenceIdIfRedemptionEnabled = createSelector(
  getRewardsAccounts,
  getSelectedAccountReferenceId,
  (accounts, selectedAccountId) => {
    const account = accounts.find(
      (account) => account.accountReferenceId === selectedAccountId
    );

    return account?.allowRewardsRedemption ?? true ? selectedAccountId : null;
  }
);

export const getSelectedAccount = createSelector(
  getRewardsAccounts,
  getSelectedAccountReferenceId,
  (rewardsAccounts, referenceId): RewardsAccount | undefined => {
    return rewardsAccounts.find(
      (account) => account.accountReferenceId === referenceId
    );
  }
);

export const getSelectedAccountIndex = createSelector(
  getRewardsAccounts,
  getSelectedAccountReferenceId,
  (accounts, id) => {
    return accounts.findIndex((account) => account.accountReferenceId === id);
  }
);

// Corporate Travel

export const getAllowRewardsRedemptionInAnyAccount = createSelector(
  getRewardsAccounts,
  (rewardsAccounts): boolean => {
    return rewardsAccounts.some(
      (account) => account.allowRewardsRedemption ?? true
    );
  }
);

export const getIsAutoApprovalEnabled = (state: IStoreState) =>
  state.vacationRentalRewards.isAutoApprovalEnabled;

export const getUserIsPrimaryCardHolder = createSelector(
  getRewardsAccounts,
  (rewardsAccounts) => {
    return rewardsAccounts.some(
      (account) => account.customerAccountRole === CustomerAccountRole.Primary
    );
  }
);

export const getAllowRewardsWithPolicy = createSelector(
  getAllowRewardsRedemptionInAnyAccount,
  (allowRewards): boolean => {
    if (isCorpTenant(config.TENANT)) {
      return allowRewards;
    }
    return true;
  }
);

export const getRewardsAccountForBanner = createSelector(
  getRewardsAccountWithLargestEarnForBanner,
  (highestEarnAccount): RewardsAccount | undefined => {
    return highestEarnAccount;
  }
);

export const getVacationRentalBookEarnValuesByRewardAcctId = (
  state: IStoreState
) => state.vacationRentalBook.earnValuesByRewardAcctId;

export const getVacationRentalBookPriceQuote = (state: IStoreState) =>
  state.vacationRentalBook.priceQuote;

export const getVacationRentalBookRewardsPaymentInFiatCurrency = (
  state: IStoreState
) => state.vacationRentalBook.rewardsPaymentInFiatCurrency;

export const getVacationRentalBookOfferToApply = (state: IStoreState) =>
  state.vacationRentalBook.offerToApply;

export const getVacationRentalBookCreditToApply = (state: IStoreState) =>
  state.vacationRentalBook.creditToApply;

export const getVacationRentalBookPaymentMethodRewardsAccountId = (
  state: IStoreState
) => state.vacationRentalBook.paymentMethodRewardsAccountId;

export const getEarnForHighestEarnRewardsAccountFromState = createSelector(
  getRewardsAccountWithLargestEarnForBanner,
  getVacationRentalBookEarnValuesByRewardAcctId,
  getVacationRentalBookPriceQuote,
  getVacationRentalBookRewardsPaymentInFiatCurrency,
  getVacationRentalBookOfferToApply,
  getVacationRentalBookCreditToApply,
  getVacationRentalBookPaymentMethodRewardsAccountId,
  (
    highestEarnRewardsAccount,
    earnValuesByRewardAcctId,
    priceQuote,
    rewardsPaymentInFiatCurrency,
    offerToApply,
    creditToApply,
    paymentMethodRewardsAccountId
  ): RewardsPrice | undefined => {
    const rewardsAccount = highestEarnRewardsAccount;

    if (!rewardsAccount) {
      return undefined;
    }

    if (rewardsAccount?.customerAccountRole !== CustomerAccountRole.Primary) {
      return undefined;
    }

    const tripTotal = priceQuote?.pricing?.tripTotal?.fiat?.value;

    if (!tripTotal) {
      const earnForAccount =
        earnValuesByRewardAcctId[rewardsAccount.accountReferenceId];

      if (!earnForAccount) {
        return undefined;
      }

      return {
        value: earnForAccount.amount,
        currency: rewardsAccount.rewardsBalance.currency,
        currencyDescription:
          rewardsAccount.rewardsBalance.currencyDescription || "miles",
      };
    }

    if (paymentMethodRewardsAccountId !== rewardsAccount.accountReferenceId) {
      return undefined;
    }

    const rewardsPayment = rewardsPaymentInFiatCurrency?.value || 0;
    const offerAmount = offerToApply?.amount?.amount || 0;
    const creditAmount = creditToApply?.amount?.amount || 0;

    const totalCreditsAndRewards = rewardsPayment + offerAmount + creditAmount;
    const creditCardPaymentRequired = Math.max(
      0,
      tripTotal - totalCreditsAndRewards
    );

    if (creditCardPaymentRequired <= 0) {
      return undefined;
    }

    const earnForAccount =
      earnValuesByRewardAcctId[rewardsAccount.accountReferenceId];

    if (!earnForAccount) {
      return undefined;
    }

    const earnAmount = earnForAccount.amount;

    return {
      value: earnAmount,
      currency: rewardsAccount.rewardsBalance.currency,
      currencyDescription:
        rewardsAccount.rewardsBalance.currencyDescription || "miles",
    };
  }
);
